import React, { useState, useEffect } from 'react';
import { api } from '@services/api';
import {
  Container,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  TableBody,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Switch,
  FormControlLabel
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { HighlightOff, SettingsBackupRestore, Business, Block, CheckCircle } from '@material-ui/icons';
import { type Color } from '@material-ui/lab/Alert';
import { Button } from '@components/Button';
import { ToastMessage } from '@components/ToastMessage';
import usePagination from '@hooks/usePagination';
import { FileX, MagnifyingGlass } from '@phosphor-icons/react';
import { Pagination } from '@material-ui/lab';
import { FilePdf } from '@phosphor-icons/react/dist/ssr';
import { AppError } from '@utils/AppError';
import { useAuth } from '@context/auth';
import { DialogActionDelete } from '../../../components/DialogAction';
import { type Contratos } from '@dtos/contratosDTO';
import { ContainerButtonsTop, DivSearch } from './styles';
import { maskDate } from '@utils/masks';
import { NavLink } from '@components/NavLink';
import { Button as MuiButton } from '@material-ui/core';

// Interface para as empresas vinculadas
interface EmpresaVinculada {
  cnpj: string;
  exibir: string;
  nome: string;
}

export interface IDialog {
  nome_contrato: string;
  cod_contrato: string;
  data_criacao: string | Date;
  data_renovacao: string | Date;
  arquivo: string[] | null;
  exame: object[];
  risco: string;
  servico: string;
  num_conv_med: string;
  num_conv_odont: string;
  num_conv_acade: string;
  num_seg_vida: string;
  num_apol_pat: string;
}

const ListaEmpresas: React.FC = () => {
  const [contratos, setContratos] = useState<Contratos[]>([]);
  const [edit, setEdit] = useState<Contratos>({} as Contratos);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState({
    cod: '',
    message: '',
    color: '' as Color,
    show: false
  });

  // Estados para o modal de empresas
  const [empresasModal, setEmpresasModal] = useState(false);
  const [empresasVinculadas, setEmpresasVinculadas] = useState<EmpresaVinculada[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [contratoSelecionado, setContratoSelecionado] = useState<Contratos>({} as Contratos);

  // Estados para os modais de confirmação
  const [confirmacaoEmpresaModal, setConfirmacaoEmpresaModal] = useState(false);
  const [empresaSelecionada, setEmpresaSelecionada] = useState<EmpresaVinculada | null>(null);
  
  const [confirmacaoContratoModal, setConfirmacaoContratoModal] = useState(false);
  const [contratoParaDesativar, setContratoParaDesativar] = useState<Contratos | null>(null);

  const [value, setValue] = useState('');
  const { user } = useAuth();

  const [page, setPage] = useState(1);
  const PER_PAGE = 30;

  const count = Math.ceil(contratos.length / PER_PAGE);
  const _DATA = usePagination(contratos, PER_PAGE);

  // Função para abrir modal de confirmação de desativação/ativação do contrato
  function abrirModalToggleContrato(contrato: Contratos) {
    setContratoParaDesativar(contrato);
    setConfirmacaoContratoModal(true);
  }

  // Função para fechar modal de confirmação do contrato
  function fecharModalContrato() {
    setConfirmacaoContratoModal(false);
    setContratoParaDesativar(null);
  }

  // Função para desativar/ativar contrato
  async function toggleContrato() {
    if (!contratoParaDesativar) return;

    try {
      const novoStatus = contratoParaDesativar.exibir?.toLowerCase() === 'sim' ? 'não' : 'sim';
      
      // Chamada para a rota de status do contrato
      await api.patch(`/ravim/contratos/status`, {
        cod_contrato: contratoParaDesativar.cod_contrato,
        exibir: novoStatus
      });

      // Atualiza a lista local
      setContratos(prevContratos =>
        prevContratos.map(contrato =>
          contrato.cod_contrato === contratoParaDesativar.cod_contrato
            ? { ...contrato, exibir: novoStatus }
            : contrato
        )
      );

      setToastMessage({
        cod: '',
        color: 'success',
        message: `Contrato ${novoStatus === 'sim' ? 'ativado' : 'desativado'} com sucesso!`,
        show: true
      });

    } catch (error) {
      console.error('Erro ao alterar status do contrato:', error);
      setToastMessage({
        cod: '',
        color: 'error',
        message: 'Erro ao alterar status do contrato',
        show: true
      });
    } finally {
      fecharModalContrato();
    }
  }

  // Função para abrir o modal de empresas vinculadas
  async function abrirModalEmpresas(contrato: Contratos) {
    setContratoSelecionado(contrato);
    setLoadingEmpresas(true);
    setEmpresasModal(true);

    try {
      const response = await api.get(`/ravim/contratos/${contrato.cod_contrato}/empresas`);
      console.log('Dados das empresas:', response.data);
      setEmpresasVinculadas(response.data);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      setToastMessage({
        cod: '',
        color: 'error',
        message: 'Erro ao carregar empresas vinculadas',
        show: true
      });
      setEmpresasVinculadas([]);
    } finally {
      setLoadingEmpresas(false);
    }
  }

  // Função para fechar o modal
  function fecharModalEmpresas() {
    setEmpresasModal(false);
    setEmpresasVinculadas([]);
    setContratoSelecionado({} as Contratos);
  }

  // Função para verificar se a empresa está ativa
  const isEmpresaAtiva = (exibir: string): boolean => {
    if (!exibir) return false;
    return exibir.toLowerCase() === 'sim';
  };

  // Função para formatar o texto do status
  const formatarStatus = (exibir: string): string => {
    if (!exibir) return 'Empresa não ativa';
    return exibir.toLowerCase() === 'sim' ? 'Empresa ativa' : 'Empresa não ativa';
  };

  // Função para abrir modal de confirmação
  const abrirModalConfirmacaoEmpresa = (empresa: EmpresaVinculada) => {
    setEmpresaSelecionada(empresa);
    setConfirmacaoEmpresaModal(true);
  };

  // Função para fechar modal de confirmação
  const fecharModalConfirmacaoEmpresa = () => {
    setConfirmacaoEmpresaModal(false);
    setEmpresaSelecionada(null);
  };

  // Função para confirmar desativação
  const confirmarDesativacaoEmpresa = async () => {
    if (empresaSelecionada) {
      await toggleStatusEmpresa(empresaSelecionada);
      fecharModalConfirmacaoEmpresa();
    }
  };

  // Função única para ativar/desativar empresa
  const toggleStatusEmpresa = async (empresa: EmpresaVinculada) => {
    try {
      const estaAtiva = isEmpresaAtiva(empresa.exibir);
      const novoStatus = estaAtiva ? 'não' : 'sim';
      
      // Chamada para a rota única de status
      await api.patch(`/ravim/empresas/status`, {
        num_cnpj: empresa.cnpj,
        exibir: novoStatus
      });

      // Atualiza a lista local
      setEmpresasVinculadas(prevEmpresas =>
        prevEmpresas.map(emp =>
          emp.cnpj === empresa.cnpj 
            ? { ...emp, exibir: novoStatus }
            : emp
        )
      );

      setToastMessage({
        cod: '',
        color: 'success',
        message: `Empresa ${novoStatus === 'sim' ? 'ativada' : 'desativada'} com sucesso!`,
        show: true
      });

    } catch (error) {
      console.error('Erro ao alterar status da empresa:', error);
      setToastMessage({
        cod: '',
        color: 'error',
        message: 'Erro ao alterar status da empresa',
        show: true
      });
    }
  };

  // Função para lidar com o clique no botão de ação
  const handleAcaoEmpresa = (empresa: EmpresaVinculada) => {
    const estaAtiva = isEmpresaAtiva(empresa.exibir);
    
    if (estaAtiva) {
      // Se está ativa, abre modal de confirmação para desativar
      abrirModalConfirmacaoEmpresa(empresa);
    } else {
      // Se está inativa, ativa diretamente (sem confirmação)
      toggleStatusEmpresa(empresa);
    }
  };

  const filterContractsPerPage =
    _DATA.currentData().length > 0
      ? _DATA
          .currentData()
          .filter(
            (contrato: Contratos) =>
              contrato.nome_contrato
                .toString()
                .toUpperCase()
                .includes(search.toUpperCase()) ||
              contrato.cod_contrato.toString().toUpperCase().includes(search.toUpperCase())
          )
      : [];

  const filterContracts =
    contratos.length > 0
      ? contratos.filter(
          (contrato: Contratos) =>
            contrato.nome_contrato
              .toString()
              .toUpperCase()
              .includes(search.toUpperCase()) ||
            contrato.cod_contrato.toString().toUpperCase().includes(search.toUpperCase())
        )
      : [];

  const filter = search.length > 0 ? filterContracts : filterContractsPerPage;

async function fetchContratos() {
  try {
    setLoading(false);
    const res = await api.get(`/ravim/contratos-empresas`);
    
    if (res.status === 404) return;
    
    // Verifique a estrutura da resposta no console
    console.log('Resposta contratos ativos:', res.data);
    
    // Ajuste conforme a estrutura real da resposta
    if (res.data && Array.isArray(res.data.data)) {
      setContratos(res.data.data);  // Se a resposta for { data: [], ... }
    } else if (Array.isArray(res.data)) {
      setContratos(res.data);       // Se a resposta for direto o array
    } else {
      console.warn('Estrutura inesperada:', res.data);
      setContratos([]);             // Fallback para array vazio
    }
    
  } catch (error) {
    const messageError = error instanceof AppError;
    const message = messageError
      ? error.message
      : 'Não foi possível carregar lista de Contratos empresa.';
    setToastMessage({
      cod: '',
      color: 'error',
      message,
      show: true
    });
    setContratos([]); // Garante que seja array vazio em caso de erro
  } finally {
    setLoading(true);
  }
}

  const handleChange = (e: any, p: number) => {
    setPage(p);
    _DATA.jump(p);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  useEffect(() => {
    fetchContratos();
  }, []);

  useEffect(() => {
    if (!value) {
      setSearch('');
    }
  }, [value]);

  return (
    <Container>
      <ContainerButtonsTop>
        <DivSearch>
          <TextField
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            label="Pesquisar Contratos"
            type="text"
            variant="outlined"
            style={{ background: '#FFF' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch(value);
              }
            }}
          />
          <Button
            variantstyle="SECUNDARY"
            icon={<MagnifyingGlass size={25} />}
            onClick={() => {
              handleSearch(value);
            }}
          />
        </DivSearch>
        <NavLink to="/cadastro-contrato-empresa">
          <Button title="Cadastrar contrato" />
        </NavLink>
      </ContainerButtonsTop>

      <Typography
        variant="h4"
        align="center"
        style={{ marginBottom: 20, fontWeight: 'bold' }}
      >
        Contratos Ativos
      </Typography>
      <Typography variant="h6" align="left">
        Total contratos Ativos: {contratos.length}
      </Typography>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20
        }}
      >
        <Pagination
          count={count}
          size="large"
          page={page}
          variant="outlined"
          shape="rounded"
          onChange={handleChange}
        />
        {loading ? (
          <TableContainer component={Paper}>
            <Table style={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Nome do Contrato</TableCell>
                  <TableCell>Código do Contrato</TableCell>
                  <TableCell>Data de Criação</TableCell>
                  <TableCell>Data de Renovação</TableCell>
                  <TableCell>Arquivo</TableCell>
                  <TableCell>ATUALIZAR</TableCell>
                  <TableCell>EMPRESAS</TableCell>
                  <TableCell>STATUS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filter &&
                  filter.map((contrato: Contratos, index: number) => {
                    return (
                      <TableRow key={index}>
                        <TableCell>{contrato.nome_contrato}</TableCell>
                        <TableCell>{contrato.cod_contrato}</TableCell>
                        <TableCell>{maskDate(contrato.data_criacao)}</TableCell>
                        <TableCell>
                          {maskDate(contrato.data_renovacao)}
                        </TableCell>
                        <TableCell>
                          {contrato.arquivo && contrato.arquivo.toString().toLowerCase() === 'true' ? (
                            <FilePdf size={30} />
                          ) : (
                            <FileX size={30} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Link
                            to={{
                              pathname: '/editar-contrato',
                              state: contrato
                            }}
                          >
                            <IconButton>
                              <SettingsBackupRestore
                                fontSize="medium"
                                style={{ color: '#422C59' }}
                              />
                            </IconButton>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => {
                              abrirModalEmpresas(contrato);
                            }}
                            title="Ver empresas vinculadas"
                          >
                            <Business
                              fontSize="medium"
                              style={{ color: '#1976d2' }}
                            />
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={contrato.exibir?.toLowerCase() === 'sim'}
                                onChange={() => {
                                  abrirModalToggleContrato(contrato);
                                }}
                                name="status-contrato"
                                color="primary"
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {contrato.exibir?.toLowerCase() === 'sim' ? 'Ativado' : 'Desativado'}
                              </Typography>
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <CircularProgress />
          </div>
        )}
        <Pagination
          count={count}
          size="large"
          page={page}
          variant="outlined"
          shape="rounded"
          onChange={handleChange}
        />
      </div>

      {/* Modal para listar empresas vinculadas */}
      <Dialog
        open={empresasModal}
        onClose={fecharModalEmpresas}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Empresas Vinculadas - {contratoSelecionado.nome_contrato}
        </DialogTitle>
        <DialogContent>
          {loadingEmpresas ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress />
            </Box>
          ) : empresasVinculadas.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome da Empresa</TableCell>
                    <TableCell>CNPJ</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {empresasVinculadas.map((empresa, index) => {
                    const estaAtiva = isEmpresaAtiva(empresa.exibir);
                    const statusFormatado = formatarStatus(empresa.exibir);
                    
                    return (
                      <TableRow key={`${empresa.cnpj}-${index}`}>
                        <TableCell>{empresa.nome}</TableCell>
                        <TableCell>{empresa.cnpj}</TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            style={{ 
                              color: estaAtiva ? 'green' : 'red',
                              fontWeight: 'bold'
                            }}
                          >
                            {statusFormatado}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={estaAtiva}
                                onChange={() => handleAcaoEmpresa(empresa)}
                                name="status-empresa"
                                color="primary"
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {estaAtiva ? 'Ativada' : 'Desativada'}
                              </Typography>
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body1" align="center" style={{ padding: 20 }}>
              Nenhuma empresa vinculada a este contrato.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton 
            onClick={fecharModalEmpresas}
            variant="contained" 
            color="secondary"
          >
            Fechar
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação para desativar empresa */}
      <Dialog
        open={confirmacaoEmpresaModal}
        onClose={fecharModalConfirmacaoEmpresa}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirmar Desativação
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Tem certeza que deseja desativar a empresa <strong>"{empresaSelecionada?.nome}"</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
            CNPJ: {empresaSelecionada?.cnpj}
          </Typography>
          <Typography variant="body2" color="error" style={{ marginTop: 10, fontWeight: 'bold' }}>
            A empresa não será exibida no sistema após a desativação.
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton 
            onClick={fecharModalConfirmacaoEmpresa}
            variant="outlined" 
            color="primary"
          >
            Cancelar
          </MuiButton>
          <MuiButton 
            onClick={confirmarDesativacaoEmpresa}
            variant="contained" 
            color="secondary"
            startIcon={<Block />}
            style={{ backgroundColor: '#d32f2f', color: '#fff' }}
          >
            Desativar Empresa
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação para desativar/ativar contrato */}
      <Dialog
        open={confirmacaoContratoModal}
        onClose={fecharModalContrato}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {contratoParaDesativar?.exibir?.toLowerCase() === 'sim' ? 'Desativar Contrato' : 'Ativar Contrato'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {contratoParaDesativar?.exibir?.toLowerCase() === 'sim' 
              ? `Tem certeza que deseja desativar o contrato "${contratoParaDesativar?.nome_contrato}"?`
              : `Tem certeza que deseja ativar o contrato "${contratoParaDesativar?.nome_contrato}"?`
            }
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
            Código: {contratoParaDesativar?.cod_contrato}
          </Typography>
          {contratoParaDesativar?.exibir?.toLowerCase() === 'sim' && (
            <Typography variant="body2" color="error" style={{ marginTop: 10, fontWeight: 'bold' }}>
              Todas as empresas vinculadas a este contrato também serão desativadas.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton 
            onClick={fecharModalContrato}
            variant="outlined" 
            color="primary"
          >
            Cancelar
          </MuiButton>
          <MuiButton 
            onClick={toggleContrato}
            variant="contained" 
            color={contratoParaDesativar?.exibir?.toLowerCase() === 'sim' ? 'secondary' : 'primary'}
            startIcon={contratoParaDesativar?.exibir?.toLowerCase() === 'sim' ? <Block /> : <CheckCircle />}
            style={{ 
              backgroundColor: contratoParaDesativar?.exibir?.toLowerCase() === 'sim' ? '#d32f2f' : '#2e7d32',
              color: '#fff' 
            }}
          >
            {contratoParaDesativar?.exibir?.toLowerCase() === 'sim' ? 'Desativar' : 'Ativar'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      <ToastMessage
        code={toastMessage.cod}
        message={toastMessage.message}
        show={toastMessage.show}
        severity={toastMessage.color}
        onClose={() => {
          setToastMessage({
            ...toastMessage,
            show: false
          });
        }}
      />
    </Container>
  );
};

export default ListaEmpresas;